import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TextStyles from "../../components/Elements/TextStyles/TextStyles";
import { ReactComponent as GreenEye } from "../../assets/svgs/greenEye.svg";
import { ReactComponent as GreenDelete } from "../../assets/svgs/delete.svg";
import { Tables } from "../../components/Elements/Tables/Tables";
import SearchBar from "../../components/Elements/Search/SearchBar";
import CustomButton from "../../components/Elements/Buttons/CustomButtons";
import CardsBorder from "../../components/Cards/CardsBorder";
import CustomSwitch from "../../components/Elements/CustomSwitch/CustomSwitch";
import PopupCard from "../../components/Cards/PopupCard/PopupCard";
import {
  fetchAllGroups,
  deleteGroups,
  updateGroupsStatus,
  setCurrentPage,
  setPageSize,
  setSearch,
  clearGroupError,
  hidePopup,
} from "../../redux/group/groupSlice";
import GroupAddNew from "./GroupAddNew";

const DEBOUNCE_DELAY = 500; // ms

const Group = () => {
  const [addGroup, setAddGroup] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    visible: false,
    groupId: null,
    groupName: "",
  });
  const [successPopup, setSuccessPopup] = useState({
    visible: false,
    title: "",
    message: "",
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ─── Redux State ─────────────────────────────────────────────────────────
  const {
    groups,
    totalRecords,
    totalPages,
    currentPage,
    pageSize,
    search,
    loading,
    error,
    popup,
    groupId,
  } = useSelector((state) => state.group);

  // ─── Local search input (debounced before dispatching to Redux) ──────────
  const [searchInput, setSearchInput] = useState(search);
  const debounceTimer = useRef(null);

  // ─── Fetch on pagination / search changes ────────────────────────────────
  useEffect(() => {
    dispatch(fetchAllGroups({ pageNo: currentPage, pageSize, search }));
  }, [dispatch, currentPage, pageSize, search]);

  // Clear error on unmount
  useEffect(() => {
    return () => dispatch(clearGroupError());
  }, [dispatch]);

  // ─── Debounced Search ─────────────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (value) => {
      setSearchInput(value);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        dispatch(setSearch(value));
      }, DEBOUNCE_DELAY);
    },
    [dispatch],
  );

  // ─── Restore Pagination State on Mount ────────────────────────────────────
  useEffect(() => {
    const savedPage = sessionStorage.getItem("groupPage");
    if (savedPage) {
      dispatch(setCurrentPage(parseInt(savedPage, 10)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Pagination ───────────────────────────────────────────────────────────
  const handlePageChange = useCallback(
    (page, size) => {
      if (size !== pageSize) dispatch(setPageSize(size));
      dispatch(setCurrentPage(page));
      sessionStorage.setItem("groupPage", page.toString());
    },
    [dispatch, pageSize],
  );

  //-----POP-UPs-----------------------------------------------------------
  useEffect(() => {
    if (popup.visible) {
      const timer = setTimeout(() => {
        dispatch(hidePopup());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [popup.visible, dispatch]);
  // ─── Table Headers ────────────────────────────────────────────────────────
  const headers = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "Group Name", key: "groupName" },
      { label: "Group ID", key: "groupId" },
      { label: "Created On", key: "createdOn" },
      { label: "Action", key: "action" },
    ],
    [],
  );
  //-------Handle Toggle-----------
  const handleStatusToggle = async (id, checked) => {
    try {
      await dispatch(
        updateGroupsStatus({
          id,
          status: checked ? "Active" : "Inactive",
        }),
      ).unwrap();
    } catch (err) {
      console.log("Status update failed", err);
    }
  };
  // ─── Table Data ───────────────────────────────────────────────────────────
  const tableData = useMemo(
    () =>
      groups.map((item, index) => ({
        serial: (currentPage - 1) * pageSize + index + 1,
        groupName: item.groupName ?? "—",
        groupId: item.groupId ?? "—",
        status: item.status ?? "—",
        createdOn: item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-IN")
          : "—",
        action: (
          <div className="flex gap-2 justify-center items-center">
            <GreenEye
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate(`/group/group-page/${item._id}`)
              }
            />
            <GreenDelete
              style={{ cursor: "pointer" }}
              onClick={() => {
                setDeleteConfirmation({
                  visible: true,
                  groupId: item._id,
                  groupName: item.groupName,
                });
              }}
            />
            <CustomSwitch
              checked={item.status === "Active"}
              onChange={(checked) => handleStatusToggle(item._id, checked)}
            />
          </div>
        ),
      })),
    [groups, currentPage, pageSize, navigate],
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      style={{ padding: "40px" }}
      className="h-screen"
    >
      <div>
        {/* Breadcrumb + Heading */}
        <div>
          <div
            className="flex justify-end text-xs"
            style={{ fontFamily: "Calibri" }}
          >
            <p>Home</p>
            <span> / </span>
            <TextStyles color="gradient">Group</TextStyles>
          </div>
          <div className="flex items-center gap-2 text-xl font-bold">
            <div className="pt-4 px-2">
              <TextStyles color="gradient">Group</TextStyles>
            </div>
          </div>
        </div>
        {/* Error Banner */}
        {error && (
          <div
            className="mb-4 px-4 py-3 rounded text-sm"
            style={{
              backgroundColor: "#FEE2E2",
              color: "#B91C1C",
              border: "1px solid #FECACA",
            }}
          >
            ⚠️ {error}
          </div>
        )}
        <CardsBorder>
          {/* Toolbar */}
          <div className="flex justify-between mb-4 px-6">
            <div className="flex gap-4 items-center">
              {/* Record count badge */}
              <div
                style={{ background: "#B6D4D933", height: "40px" }}
                className="px-4 border  border-gray-300 rounded-md text-gray-400 text-[14px] font-light flex items-center"
              >
                {/* <p
                  style={{
                    fontFamily: "Calibri",
                    fontWeight: "300",
                    fontSize: "16px",
                    lineHeight: "24px",
                    margin: 0,
                  }}
                > */}
                  TOTAL GROUPS {loading ? "..." : totalRecords}
                {/* </p> */}
              </div>

              {/* Debounced search */}
              <SearchBar
                width="300px"
                value={searchInput}
                onSearch={handleSearchChange}
              />
            </div>

            <div className="flex gap-4">
              <CustomButton
                buttonType="primary"
                variant="ok"
                buttonText="+ ADD NEW"
                className={"py-6 text-xs font-medium"}
                onClick={() => {
                  setAddGroup(true);
                }}
              />
            </div>
          </div>

          {/* Table */}
          <Tables
            data={tableData}
            headers={headers}
            loading={loading}
            pageSize={pageSize}
            total={totalRecords}
            current={currentPage}
            onChange={handlePageChange}
          />
        </CardsBorder>{" "}
        {addGroup && (
          <GroupAddNew
            onOpen={addGroup}
            onClose={() => setAddGroup(false)}
            onSave={() => {
              dispatch(
                fetchAllGroups({ pageNo: currentPage, pageSize, search }),
              );
              setAddGroup(false);
            }}
          />
        )}
        {popup.visible && (
          <div className="fixed inset-0  bg-black/30 z-[9999] flex items-center justify-center">
            <PopupCard
              type={popup.type}
              title={popup.title}
              message={popup.message}
              onClose={() => dispatch(hidePopup())}
            />
          </div>
        )}
        {/*------------- DELETE GROUP------- */}
        {deleteConfirmation.visible && (
          <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
            <PopupCard
              type="warning"
              title="Confirm Delete"
              message={`Are you sure you want to delete ${deleteConfirmation.groupName}?`}
              showActions={true}
              confirmText="Yes, Delete"
              cancelText="Cancel"
              onCancel={() =>
                setDeleteConfirmation({
                  visible: false,
                  groupId: null,
                  groupName: "",
                })
              }
              onConfirm={async () => {
                try {
                  await dispatch(
                    deleteGroups(deleteConfirmation.groupId),
                  ).unwrap();


                  setDeleteConfirmation({
                    visible: false,
                    groupId: null,
                    groupName: "",
                  });

                  setSuccessPopup({
                    visible: true,
                    title: "Deleted",
                    message: `${deleteConfirmation.groupName} has been deleted successfully!`,
                  });

                  setTimeout(() => {
                    setSuccessPopup({
                      visible: false,
                      title: "",
                      message: "",
                    });
                  }, 3000);
                } catch (err) {
                  console.error("Failed to delete group:", err);
                }
              }}
            />
          </div>
        )}
        {successPopup.visible && (
          <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
            <PopupCard
              type="success"
              title={successPopup.title}
              message={successPopup.message}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Group;
